// chatRecommender — pick the right *mode* and *model* for the next
// send, based on the recent conversation + current draft.
//
// Two-tier design (matches the plan locked in with the user):
//
//   1. **Heuristic** (this file, pure + sync): cheap keyword / shape
//      analysis on the last few turns + the draft. Catches the obvious
//      cases ("draw a logo", "research X", "summarise this code")
//      instantly with **no AI call**. Returns a `confidence` in [0, 1].
//   2. **AI classifier** (edge function `recommend-chat`): only invoked
//      when the heuristic confidence is below `LOW_CONFIDENCE`. Lets
//      Gemini Flash Lite settle the ambiguous middle ground.
//
// The recommender NEVER decides for the user. It returns a
// recommendation; the chat shell decides whether to silently switch
// the model (UX choice: "auto-switch silently") or surface a chip
// for one-click mode switch (UX choice: "Suggest + one-click switch,
// stay in chat").

export type RecommenderMode =
  | "chat"
  | "image"
  | "research"
  | "analyze"
  | "builder";

export interface ChatTurnLite {
  role: "user" | "assistant";
  content: string;
}

export interface Recommendation {
  /** Suggested mode (omitted when current mode is fine). */
  mode?: RecommenderMode;
  /**
   * Suggested chat model id (omitted when current model is fine).
   * Always one of the ids in `AVAILABLE_MODELS`. Image/research
   * recommendations don't carry a model — those modes pick their own.
   */
  model?: string;
  /** Short, human-readable rationale shown to the user. */
  reason: string;
  /**
   * Heuristic confidence in [0, 1]. The chat shell uses this to decide
   * whether to additionally call the AI classifier. A value at or
   * above `LOW_CONFIDENCE` means "trust the heuristic, don't pay for
   * an LLM call".
   */
  confidence: number;
}

/** Below this, the shell should fall back to the AI classifier. */
export const LOW_CONFIDENCE = 0.6;

// --- Pattern banks ----------------------------------------------------
//
// Word-boundary matches keep "imagine" from triggering the image mode
// and "researcher" from triggering research. Patterns are intentionally
// small + opinionated — false positives lose user trust faster than
// false negatives, so we err on the side of staying silent.

const IMAGE_PATTERNS: RegExp[] = [
  /\b(draw|sketch|illustrate|paint|render|generate|create|make)\s+(an?|the)?\s*(image|picture|photo|logo|icon|illustration|poster|banner|wallpaper|mockup|sticker|avatar|portrait|scene|cover)\b/i,
  /\b(image of|picture of|photo of|logo for|icon for)\b/i,
  /\b(midjourney|dall[- ]?e|nano banana|stable diffusion)\b/i,
];

const RESEARCH_PATTERNS: RegExp[] = [
  /\b(latest|newest|recent|today|this week|this month|2024|2025|2026)\b.*\b(news|update|release|launch|trend|stats?|statistics|data|study|paper|article)\b/i,
  /\b(google|search|look up|find out|sources for|cite|citation|references?)\b/i,
  /\b(what'?s new|current state of|state of the art)\b/i,
  /\bweb[- ]?search\b/i,
];

const ANALYZE_PATTERNS: RegExp[] = [
  /\b(summari[sz]e|tl;?dr|recap|condense|outline)\s+(this|the|that|my|above)/i,
  /\b(extract|pull out|list)\s+(the\s+)?(action items|todos|key points|takeaways|entities)/i,
  /\b(refactor|clean up|optimi[sz]e)\s+(this|the|my|above)\s+(code|function|snippet)/i,
];

// Heavy-reasoning signals — long prompts, math, multi-step plans,
// "compare/critique/architect" verbs. These nudge toward a Pro/Sonnet
// tier model.
const REASONING_PATTERNS: RegExp[] = [
  /\b(architect|design|plan|critique|compare|trade[- ]?offs?|analyze|reason about|prove|derive)\b/i,
  /\b(why does|why would|explain in depth|step by step|chain of thought)\b/i,
];

// Code-specific signals — fenced blocks or strong code verbs. Pushes
// toward Claude Sonnet (best agentic + code) when long, or Haiku for
// short helpers.
const CODE_PATTERNS: RegExp[] = [
  /```/,
  /\b(write|debug|fix|implement|refactor)\s+(a|an|my|the)?\s*(function|class|component|hook|endpoint|migration|sql|query|test|script)\b/i,
  /\b(typescript|javascript|python|rust|go|sql|react|nextjs|vue|svelte)\b/i,
];

// Quick, conversational asks — pushes toward the cheap/fast tier.
const SIMPLE_PATTERNS: RegExp[] = [
  /^(hi|hey|hello|thanks?|thank you|ok|okay|cool|nice|got it)\b/i,
  /^\?+$/,
];

// --- Public API -------------------------------------------------------

export interface RecommendInput {
  /** Last few turns (most recent last). Heuristic only inspects up to 6. */
  history: ChatTurnLite[];
  /** What the user is *about* to send. */
  draft: string;
  /** Current shell mode. */
  currentMode: RecommenderMode;
  /** Currently selected chat model id. */
  currentModel: string;
}

/**
 * Synchronous heuristic. Returns a recommendation OR null if nothing
 * is worth surfacing. Callers should treat null as "no change".
 */
export function heuristicRecommend(input: RecommendInput): Recommendation | null {
  const { draft, currentMode, currentModel, history } = input;
  const text = draft.trim();
  if (text.length === 0) return null;

  // ---- Mode recommendations (highest priority) ----------------------
  if (currentMode !== "image" && IMAGE_PATTERNS.some((re) => re.test(text))) {
    return {
      mode: "image",
      reason: "This looks like an image request — switch to Image mode?",
      confidence: 0.9,
    };
  }
  if (currentMode !== "research" && RESEARCH_PATTERNS.some((re) => re.test(text))) {
    return {
      mode: "research",
      reason: "Looks like a web-grounded question — switch to Research?",
      confidence: 0.8,
    };
  }
  if (currentMode !== "analyze" && ANALYZE_PATTERNS.some((re) => re.test(text))) {
    return {
      mode: "analyze",
      reason: "Looks like a summarise/extract task — switch to Analyze?",
      confidence: 0.75,
    };
  }

  // ---- Model recommendations (only when we're staying in chat) ------
  if (currentMode === "chat") {
    // Recent conversation length used to weight reasoning suggestions —
    // a long thread of code questions deserves a Pro model.
    const recent = history.slice(-6);
    const totalChars =
      recent.reduce((acc, t) => acc + t.content.length, 0) + text.length;
    const hasCode = CODE_PATTERNS.some((re) =>
      [text, ...recent.map((r) => r.content)].some((c) => re.test(c)),
    );
    const hasReasoning = REASONING_PATTERNS.some((re) => re.test(text));
    const isSimple = SIMPLE_PATTERNS.some((re) => re.test(text)) && text.length < 60;

    if (isSimple && currentModel !== "google/gemini-2.5-flash-lite") {
      return {
        model: "google/gemini-2.5-flash-lite",
        reason: "Short/simple message — switching to a cheaper, faster model.",
        confidence: 0.7,
      };
    }
    if (hasCode && totalChars > 1500 && currentModel !== "anthropic/claude-sonnet-4-5") {
      return {
        model: "anthropic/claude-sonnet-4-5",
        reason: "Heavy code task — switching to Claude Sonnet for stronger code reasoning.",
        confidence: 0.75,
      };
    }
    if (hasReasoning && totalChars > 800 && currentModel !== "google/gemini-2.5-pro") {
      return {
        model: "google/gemini-2.5-pro",
        reason: "Deep reasoning ahead — switching to Gemini 2.5 Pro.",
        confidence: 0.7,
      };
    }
  }

  // Nothing strong enough — let the caller decide whether to fall back
  // to the AI classifier.
  return null;
}